import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaPhone, FaSpinner, FaCheck, FaArrowLeft, FaShieldAlt, 
  FaUserCheck, FaMobileAlt
} from 'react-icons/fa';
import { sendOTP, verifyOTP, setPhone, clearOTP, clearError, clearMessage } from '../../redux/slices/authSlice';
import { getColors } from '../../styles/colors';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { isLoading, error, otpSent, phone, isAuthenticated, message, isNewUser } = useSelector((state) => state.auth);
  
  const [phoneInput, setPhoneInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [localError, setLocalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ✅ Redirect based on user type after login
  useEffect(() => {
    if (isAuthenticated) {
      setSuccessMessage('Login successful!');
      
      setTimeout(() => {
        dispatch(clearOTP());
        
        const isNew = localStorage.getItem('isNewUser') === 'true';
        if (isNew) {
          navigate('/profile-form');
        } else {
          navigate('/');
        }
      }, 1000);
    }
  }, [isAuthenticated, dispatch, navigate]);

  // ✅ Set phone from state when available
  useEffect(() => {
    if (phone) {
      setPhoneInput(phone);
    }
    dispatch(clearError());
    dispatch(clearMessage());
  }, [phone, dispatch]);

  // ✅ Timer for OTP resend
  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  // ✅ Show success message when OTP sent
  useEffect(() => {
    if (message && otpSent) {
      setSuccessMessage(message);
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  }, [message, otpSent]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 10);
  };

  const handleSendOTP = async () => {
    if (!phoneInput || phoneInput.length < 10) {
      setLocalError('Please enter a valid 10-digit phone number');
      return;
    }
    
    setLocalError('');
    setSuccessMessage('');
    dispatch(clearError());
    dispatch(clearMessage());
    dispatch(setPhone(phoneInput));
    
    try {
      const result = await dispatch(sendOTP(phoneInput)).unwrap();
      if (result) {
        setShowOtpInput(true);
        setTimer(30);
        setCanResend(false);
        setOtpInput('');
        setSuccessMessage('OTP sent successfully! Check your phone.');
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      if (err && typeof err === 'string' && err.toLowerCase().includes('spam')) {
        setLocalError('Too many OTP attempts. Please wait 5 minutes before trying again.');
      } else if (err && typeof err === 'string') {
        setLocalError(err);
      } else if (err?.message) {
        setLocalError(err.message);
      } else {
        setLocalError('Failed to send OTP. Please try again.');
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpInput || otpInput.length < 4) {
      setLocalError('Please enter the 6-digit OTP');
      return;
    }
    
    setLocalError('');
    setSuccessMessage('');
    dispatch(clearError());
    dispatch(clearMessage());
    
    try {
      const result = await dispatch(verifyOTP({ phone: phoneInput, otp: otpInput })).unwrap();
      
      console.log('✅ Login result:', result);
      
      // ✅ Store new user flag
      if (result.is_new_user) {
        localStorage.setItem('isNewUser', 'true');
        console.log('🆕 New user detected');
      } else {
        localStorage.setItem('isNewUser', 'false');
        console.log('👤 Existing user');
      }
      
    } catch (err) {
      console.error('Verify OTP error:', err);
      if (err && typeof err === 'string') {
        setLocalError(err);
      } else if (err?.message) {
        setLocalError(err.message);
      } else {
        setLocalError('Invalid OTP. Please try again.');
      }
    }
  };

  const handleResendOTP = () => {
    if (canResend) {
      dispatch(clearError());
      dispatch(clearMessage());
      dispatch(sendOTP(phoneInput));
      setTimer(30);
      setCanResend(false);
      setSuccessMessage('OTP resent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleBackToPhone = () => {
    setShowOtpInput(false);
    setOtpInput('');
    setLocalError('');
    setSuccessMessage('');
    dispatch(clearError());
    dispatch(clearMessage());
    dispatch(clearOTP());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (!showOtpInput) {
        handleSendOTP();
      } else {
        handleVerifyOTP();
      }
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ backgroundColor: colors.background }}
    >
      <motion.div
        className="rounded-2xl p-8 max-w-md w-full"
        initial={{ scale: 0.9, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:scale-105 mb-4"
          style={{ color: colors.primary }}
        >
          <FaArrowLeft className="text-xs" /> Back to Home
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: colors.primary + '20' }}>
            <FaUserCheck style={{ color: colors.primary }} />
          </div>
          <div>
            <h3 className="text-xl font-bold" style={{ color: colors.text }}>
              {showOtpInput ? 'Verify OTP' : 'Welcome to Foodies!'}
            </h3>
            <p className="text-xs" style={{ color: colors.textSecondary }}>
              {showOtpInput 
                ? `Enter the OTP sent to +91 ${phoneInput}`
                : 'Login to continue to your account'
              }
            </p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="p-3 rounded-xl mb-4 text-sm flex items-center gap-2" style={{ 
            backgroundColor: '#F0FDF4',
            color: '#16A34A',
            border: '1px solid #BBF7D0'
          }}>
            <FaCheck className="text-sm" />
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {(error || localError) && (
          <div className="p-3 rounded-xl mb-4 text-sm" style={{ 
            backgroundColor: '#FEF2F2',
            color: '#DC2626',
            border: '1px solid #FECACA'
          }}>
            {error || localError}
          </div>
        )}

        {/* Phone Input */}
        {!showOtpInput ? (
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Phone Number
            </label>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-3 py-2.5 rounded-xl" style={{ 
                backgroundColor: colors.inputBackground,
                border: `1px solid ${colors.border}`
              }}>
                <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>+91</span>
              </div>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(formatPhoneNumber(e.target.value))}
                placeholder="98765 43210"
                className="flex-1 p-3 rounded-xl text-sm transition-colors outline-none"
                style={{
                  backgroundColor: colors.inputBackground,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                }}
                onKeyDown={handleKeyDown}
                autoFocus
                maxLength={10}
              />
            </div>
            <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
              We'll send you a 6-digit OTP for verification
            </p>
          </div>
        ) : (
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Enter OTP
            </label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="flex-1 p-3 rounded-xl text-center text-lg font-bold tracking-widest transition-colors outline-none"
                style={{
                  backgroundColor: colors.inputBackground,
                  color: colors.text,
                  border: `1px solid ${otpInput.length === 6 ? '#22C55E' : colors.border}`,
                }}
                onKeyDown={handleKeyDown}
                autoFocus
                maxLength={6}
              />
              {otpInput.length === 6 && (
                <FaCheck className="text-green-500 text-xl" />
              )}
            </div>
            
            <div className="flex items-center justify-between mt-2 text-sm">
              <span style={{ color: colors.textSecondary }}>
                {canResend ? (
                  <button
                    onClick={handleResendOTP}
                    className="font-medium hover:underline transition-colors"
                    style={{ color: colors.primary }}
                  >
                    Resend OTP
                  </button>
                ) : (
                  `Resend in ${timer}s`
                )}
              </span>
              <button
                onClick={handleBackToPhone}
                className="text-xs hover:underline transition-colors"
                style={{ color: colors.textSecondary }}
              >
                Change number
              </button>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={showOtpInput ? handleVerifyOTP : handleSendOTP}
          disabled={isLoading || (showOtpInput ? otpInput.length < 4 : phoneInput.length < 10)}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ 
            backgroundColor: colors.primary,
            boxShadow: `0 4px 20px ${colors.primary}40`
          }}
        >
          {isLoading ? (
            <>
              <FaSpinner className="animate-spin" />
              {showOtpInput ? 'Verifying...' : 'Sending...'}
            </>
          ) : (
            <>
              {showOtpInput ? (
                <>
                  <FaCheck /> Verify & Login
                </>
              ) : (
                <>
                  <FaPhone /> Send OTP
                </>
              )}
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-2 mt-4 text-xs" style={{ color: colors.textSecondary }}>
          <FaShieldAlt className="text-[10px]" />
          Secured with 256-bit encryption
        </div>

        <p className="text-xs text-center mt-4" style={{ color: colors.textSecondary + '80' }}>
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </motion.div>
    </div>
  );
};

export default Login;